import React, { ReactNode } from 'react'
import { v4 as uuid } from 'uuid'
import { useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { ErrorView } from '../../../views/error/ErrorView'
import { IllustrationChoices as Illustration } from '../../common/Illustration'
import { ChatErrorState, chatErrorStateAtom } from '../atom'
import { ButtonVariant } from '../../common/Button'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { ActiveTabBar } from '../../../views/chat'
import { ChatHeaderButtons } from '../ChatHeaderButtons'
import { i18nSocietyType } from '../../../i18n'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'

interface ChatRoomErrorWrapperProps {
  activeTabBar: ActiveTabBar
  activeTab: number
  children: ReactNode
}

export const ChatRoomErrorWrapper = ({
  activeTab,
  activeTabBar,
  children,
}: ChatRoomErrorWrapperProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const [chatErrorState] = useAtom(chatErrorStateAtom)
  const navigate = useNavigate()
  const { society } = useCurrentSociety()

  switch (chatErrorState) {
    case ChatErrorState.PendingRequest:
      return (
        <ErrorView
          title={translate('common.errorViews.pendingRequest.title')}
          subtitle={translate('chatRooms.pendingRequest.subtitle')}
          illustration={Illustration.WAITING}
          buttons={[
            {
              id: uuid(),
              button: {
                label: translate(
                  'societyView.emptyState.pendingRequest.buttonTitle',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                ),
                onClick: () => navigate(reverseUrl('find-society')),
                variant: ButtonVariant.PRIMARY,
              },
            },
          ]}
          className="bg-neutral-96 px-4 pb-32"
        />
      )
    case ChatErrorState.NoSociety:
      return (
        <ErrorView
          title={translate('feedView.emptyState.noSociety.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          subtitle={translate('feedView.emptyState.noSociety.subtitle')}
          illustration={Illustration.EMPTY}
          className="bg-neutral-96 px-4 pb-32"
          buttonClassName="flex flex-wrap justify-center gap-4"
          buttons={[
            {
              id: uuid(),
              button: {
                label: translate('feedView.emptyState.noSociety.buttonTitle', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                }),
                onClick: () => navigate(reverseUrl('find-society')),
                variant: ButtonVariant.DEFAULT,
              },
            },
            {
              id: uuid(),

              button: {
                label: translate(
                  'feedView.emptyState.noSociety.createSocietyButton',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                ),
                onClick: () => navigate(reverseUrl('create-society:pick-type')),
                variant: ButtonVariant.PRIMARY,
              },
            },
          ]}
        />
      )
    case ChatErrorState.ChatDisabled:
      return (
        <ErrorView
          title={translate('chatRooms.chatDisabled.title')}
          subtitle={translate('chatRooms.chatDisabled.subtitle')}
          illustration={Illustration.EMPTY}
          className="bg-neutral-96 px-4 pb-32"
        />
      )
    case ChatErrorState.EmptyChat:
      return (
        <ErrorView
          title={translate('chatRooms.userHasNoChats.title')}
          subtitle={translate('chatRooms.userHasNoChats.subtitle')}
          illustration={Illustration.EMPTY}
          className="bg-neutral-96 px-4 pb-32"
        />
      )
    case ChatErrorState.EmptyGroupChat:
      return (
        <ErrorView
          title={translate('chatRooms.userHasNoChats.title')}
          subtitle={translate('chatRooms.userHasNoGroupChats.subtitle')}
          illustration={Illustration.EMPTY}
          className="bg-neutral-96 px-4 pb-32"
        />
      )

    case ChatErrorState.EmptyBoardChat:
      return (
        <ErrorView
          title={translate('chatRooms.noBoardChats.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          subtitle={translate('chatRooms.noBoardChats.subtitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          illustration={Illustration.EMPTY}
          className="bg-neutral-96 px-4 pb-32"
        />
      )
    case ChatErrorState.NoChatSelected:
      return (
        <ErrorView
          title={translate('chatRooms.noChatSelected.title')}
          subtitle={translate('chatRooms.noChatSelected.subtitle')}
          illustration={Illustration.CONVERSATION}
          className="bg-neutral-96 px-4 pb-32"
          custom={
            <ChatHeaderButtons
              className="mt-6 gap-4"
              activeTabBar={activeTabBar}
              activeTab={activeTab}
            />
          }
        />
      )
    default:
      return <>{children}</>
  }
}
